import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Calendar, Save, ShieldCheck, Key, Plus, Trash2, Copy, Eye, Lock, FileDown, Shield, FlaskConical, Power } from "lucide-react";
import api, { handleApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/context/AuthContext";
import ChangePassword from "@/components/ChangePassword";
import { isCompeFormat } from "@/data/banks";

/**
 * Espelhos EXATOS de `services.validation.validate_cnab_agency` e
 * `validate_cnab_account` (só dígitos — 1 a 5 na agência, 1 a 12 na conta —,
 * nunca só zeros). Régua de FORMATO: é o que decide se o save passa no schema;
 * a régua de PRESENÇA é da geração do arquivo, não daqui.
 *
 * Um construtor só para os dois campos porque a régua é a mesma menos o teto —
 * duas cópias é como elas divergem em silêncio. Os espelhos NÃO têm trava
 * automática (o front não tem suíte neste repo): mudou a régua no backend,
 * mude aqui.
 */
const cnabDigitosFormat = (max) => (value) => {
  const texto = String(value ?? "").trim();
  return new RegExp(`^[0-9]{1,${max}}$`).test(texto) && texto.replace(/0/g, "") !== "";
};
const isAgenciaCnabFormat = cnabDigitosFormat(5);
const isContaCnabFormat = cnabDigitosFormat(12);

/**
 * `cnab_bank_code` legado (ISPB de 8 dígitos, gravado quando o campo aceitava 10
 * caracteres) é carregado CRU e VISÍVEL, e trava o save até o operador corrigir.
 *
 * Duas saídas foram descartadas, ambas ruins:
 * - reenviar o valor cru: com o schema em COMPE-3 vira 422 e o save não passa
 *   de qualquer forma (desde A-R3d o 422 volta com campo+mensagem, então
 *   deixou de ser o brick opaco "Dados inválidos" — mas continua sendo uma
 *   ida ao servidor para saber o que a tela já sabe);
 * - sanear no load (`"60701190"` → `"607"`): é a MESMA truncagem silenciosa que
 *   este round mata no `_rjust_zero` do CNAB. "607" é um COMPE plausível e
 *   provavelmente de OUTRO banco, e qualquer save não-bancário desta tela o
 *   persistiria no banco pagador da remessa.
 *
 * Fica o terceiro caminho: bloquear até correção EXPLÍCITA, com o caminho de
 * correção na própria tela — e sem NENHUM ponto de truncamento (nem no load, nem
 * no `maxLength`, nem no `onChange`), porque qualquer um deles devolve um COMPE
 * de 3 dígitos válido e de outro banco, que destrava o save.
 *
 * `cnab_agency` (A-R3e-xiii, owner 2026-08-03) e `cnab_account` (A-R3e-xiv,
 * mesmo dia — o campo já existia na tela e passou a seguir o desenho ratificado
 * do vizinho) seguem o MESMO desenho, com DOIS estados distintos de propósito:
 *
 * - VAZIO ⇒ AVISO, não bloqueio. Os dois campos passaram a ser obrigatórios
 *   para GERAR o CNAB (antes saíam zeros no header e o banco não tinha como
 *   debitar a conta da empresa), mas não há sinal barato de "esta empresa usa
 *   CNAB": o modelo não tem flag e todas as empresas veem esta seção. Bloquear
 *   o save reprovaria quem paga só por PIX e mexeu numa configuração de outra
 *   seção. Então o aviso aparece no campo e a falha real fica na geração, que é
 *   onde a agência e a conta de fato importam.
 * - PREENCHIDO e fora do formato (letra, separador, dígitos demais, só zeros) ⇒
 *   ERRO BLOQUEANTE, exatamente como o código do banco: o schema
 *   (`services.validation.validate_cnab_agency` / `validate_cnab_account`)
 *   recusa esse valor e reenviá-lo viraria um 422 achatado em "Dados
 *   inválidos", brickando o save das OUTRAS configurações. O valor legado é
 *   mostrado CRU (nunca saneado no load, nunca truncado por `maxLength`) e o
 *   operador corrige explicitamente.
 *
 * REGRA ÚNICA dos campos bancários desta seção (GRAVE do parecer OC, 2026-08-03):
 * **nenhuma transformação silenciosa do que o operador digita** — nem truncar,
 * nem filtrar caracteres. O filtro de dígitos (`replace(/\D/g, "")`) parecia
 * inofensivo e é o mesmo vetor do `.slice()`: no legado da agência, que costuma
 * trazer o dígito verificador ("0001-2"), uma tecla qualquer refiltrava a string
 * para "00012" — agência VÁLIDA, DIFERENTE e com o save destravado. No código do
 * banco o filtro raramente produzia um valor válido; na agência, quase sempre.
 * Por coerência (uma regra, não duas) o filtro saiu dos DOIS campos: o valor
 * entra cru, fica visível, e o gate de formato bloqueia com erro que ensina.
 *
 * O `maxLength={20}` da CONTA saiu pela MESMA regra (A-R3e-xiv): a coluna é
 * `String(20)` e o atributo parecia só espelhá-la, mas o schema aceita no
 * máximo 12 dígitos — o corte silencioso em 20 nunca produziria um valor
 * válido a mais, e cortar um legado longo enquanto o operador digita é o vetor
 * do `.slice()` de novo. Os campos de DÍGITO (`maxLength={2}`) ficam: ali o
 * corte não muda o número da conta, e o DV é um campo de 1 posição no layout.
 */

export default function CompanySettings() {
  const { canEdit } = usePermission("configuracoes");
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalForm, setOriginalForm] = useState(null);
  const [form, setForm] = useState({
    approval_required: true, anticipate_pf: true, anticipate_pj: false,
    payment_flow_pf_type: "business_day", payment_flow_pf_day: "5",
    payment_flow_pj_type: "business_day", payment_flow_pj_day: "5",
    custody_pf: false, custody_pj: false,
    lock_employee_bank_pf: false, lock_employee_bank_pj: false,
    cnab_bank_code: "", cnab_agency: "", cnab_agency_digit: "",
    cnab_account: "", cnab_account_digit: "", cnab_convenio: "", cnab_company_name: "",
  });

  const loadCompany = useCallback(async () => {
    try {
      const res = await api.get("/companies");
      const c = res.data[0];
      if (c) {
        setCompany(c);
        const pfFlow = c.payment_flow_pf || { type: "business_day", day: 5 };
        const pjFlow = c.payment_flow_pj || { type: "business_day", day: 5 };
        setForm({
          approval_required: c.approval_required !== false,
          anticipate_pf: c.anticipate_pf !== false,
          anticipate_pj: c.anticipate_pj === true,
          payment_flow_pf_type: pfFlow.type,
          payment_flow_pf_day: String(pfFlow.day),
          payment_flow_pj_type: pjFlow.type,
          payment_flow_pj_day: String(pjFlow.day),
          custody_pf: c.custody_pf === true,
          custody_pj: c.custody_pj === true,
          lock_employee_bank_pf: c.lock_employee_bank_pf === true,
          lock_employee_bank_pj: c.lock_employee_bank_pj === true,
          cnab_bank_code: c.cnab_bank_code || "",
          cnab_agency: c.cnab_agency || "",
          cnab_agency_digit: c.cnab_agency_digit || "",
          cnab_account: c.cnab_account || "",
          cnab_account_digit: c.cnab_account_digit || "",
          cnab_convenio: c.cnab_convenio || "",
          cnab_company_name: c.cnab_company_name || "",
        });
        setOriginalForm({
          approval_required: c.approval_required !== false,
          anticipate_pf: c.anticipate_pf !== false,
          anticipate_pj: c.anticipate_pj === true,
          payment_flow_pf_type: pfFlow.type,
          payment_flow_pf_day: String(pfFlow.day),
          payment_flow_pj_type: pjFlow.type,
          payment_flow_pj_day: String(pjFlow.day),
          custody_pf: c.custody_pf === true,
          custody_pj: c.custody_pj === true,
          lock_employee_bank_pf: c.lock_employee_bank_pf === true,
          lock_employee_bank_pj: c.lock_employee_bank_pj === true,
          cnab_bank_code: c.cnab_bank_code || "",
          cnab_agency: c.cnab_agency || "",
          cnab_agency_digit: c.cnab_agency_digit || "",
          cnab_account: c.cnab_account || "",
          cnab_account_digit: c.cnab_account_digit || "",
          cnab_convenio: c.cnab_convenio || "",
          cnab_company_name: c.cnab_company_name || "",
        });
      }
    } catch (err) { toast.error(handleApiError(err, "Erro ao carregar configurações")); }
    setLoading(false);
  }, []);

  useEffect(() => { loadCompany(); }, [loadCompany]);

  const hasChanges = originalForm && JSON.stringify(form) !== JSON.stringify(originalForm);
  const cnabBankCodeInvalid = !!form.cnab_bank_code && !isCompeFormat(form.cnab_bank_code);
  // `String(... ?? "")` antes de qualquer `.trim()`: o load normaliza com `|| ""`
  // e o estado inicial é string, mas o valor vem de uma API que pode devolver
  // `cnab_agency: null`. Um `.trim()` sobre null derruba a TELA INTEIRA de
  // configurações — custo de uma normalização defensiva contra uma tela branca.
  const cnabAgency = String(form.cnab_agency ?? "").trim();
  const cnabAccount = String(form.cnab_account ?? "").trim();
  // Dois estados, dois efeitos: fora do formato BLOQUEIA o save (o schema
  // recusaria com 422 opaco); vazio só AVISA (a exigência é da geração do CNAB).
  const cnabAgencyInvalid = !!cnabAgency && !isAgenciaCnabFormat(cnabAgency);
  const cnabAgencyMissing = !cnabAgency;
  const cnabAccountInvalid = !!cnabAccount && !isContaCnabFormat(cnabAccount);
  const cnabAccountMissing = !cnabAccount;

  useEffect(() => {
    window.__unsavedChanges = hasChanges;
    return () => { window.__unsavedChanges = false; };
  }, [hasChanges]);

  useEffect(() => {
    const handler = (e) => {
      if (hasChanges) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  const handleSave = async () => {
    // Gate ANTES do PUT (padrão do AdminCredores): feedback imediato, sem
    // round-trip. O 422 do servidor já volta com campo+mensagem (A-R3d), então
    // este gate deixou de ser a ÚNICA fonte da lição — virou o atalho.
    if (cnabBankCodeInvalid) {
      toast.error(
        `Código do Banco (CNAB): o valor gravado "${form.cnab_bank_code}" está fora do padrão ` +
        "COMPE-3. Informe o código de 3 dígitos do banco pagador (ex.: 341) para salvar."
      );
      return;
    }
    // Agência/conta VAZIAS não entram aqui de propósito: só o valor FORA DO
    // FORMATO bloqueia (o schema o recusaria). Ver o comentário no topo do arquivo.
    if (cnabAgencyInvalid) {
      toast.error(
        `Agência (CNAB): o valor gravado "${cnabAgency}" está fora do padrão. ` +
        "Informe de 1 a 5 dígitos da agência do banco pagador (ex.: 0102), sem o dígito " +
        "verificador, para salvar."
      );
      return;
    }
    if (cnabAccountInvalid) {
      toast.error(
        `Conta (CNAB): o valor gravado "${cnabAccount}" está fora do padrão. ` +
        "Informe de 1 a 12 dígitos da conta do banco pagador (ex.: 0012345), sem o dígito " +
        "verificador, para salvar."
      );
      return;
    }
    setSaving(true);
    try {
      await api.put(`/companies/${company.id}`, {
        approval_required: form.approval_required,
        anticipate_pf: form.anticipate_pf,
        anticipate_pj: form.anticipate_pj,
        custody_pf: form.custody_pf,
        custody_pj: form.custody_pj,
        lock_employee_bank_pf: form.lock_employee_bank_pf,
        lock_employee_bank_pj: form.lock_employee_bank_pj,
        payment_flow_pf: { type: form.payment_flow_pf_type, day: parseInt(form.payment_flow_pf_day) || 5 },
        payment_flow_pj: { type: form.payment_flow_pj_type, day: parseInt(form.payment_flow_pj_day) || 5 },
        cnab_bank_code: form.cnab_bank_code || null,
        // Vai o valor NORMALIZADO (só `trim`, nunca filtro/corte) — é exatamente
        // o que o gate acima validou; o backend faz o mesmo `strip()`.
        cnab_agency: cnabAgency || null,
        cnab_agency_digit: form.cnab_agency_digit || null,
        cnab_account: cnabAccount || null,
        cnab_account_digit: form.cnab_account_digit || null,
        cnab_convenio: form.cnab_convenio || null,
        cnab_company_name: form.cnab_company_name || null,
      });
      toast.success("Configurações salvas!");
      setOriginalForm({ ...form });
      window.__unsavedChanges = false;
      loadCompany();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6 lg:space-y-8" data-testid="company-settings">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configurações</h1>
        <p className="text-sm text-muted-foreground mt-2">{company?.name}</p>
      </div>

      <Card className="border-white/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-orange-400" /> Aprovação de Antecipações
          </CardTitle>
          <CardDescription>Defina se as solicitações de antecipação precisam de aprovação manual</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/5">
            <div>
              <p className="text-sm font-medium">Requerer aprovação manual por antecipação?</p>
              <p className="text-xs text-muted-foreground mt-1">
                {form.approval_required
                  ? "Cada solicitação de antecipação precisará ser aprovada manualmente pela empresa"
                  : "As antecipações serão processadas automaticamente, sem necessidade de aprovação"}
              </p>
            </div>
            <Switch data-testid="settings-approval-switch"
              checked={form.approval_required}
              disabled={!canEdit}
              onCheckedChange={(v) => setForm({ ...form, approval_required: v })} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-4 h-4 text-purple-400" /> Custódia de Pagamentos
          </CardTitle>
          <CardDescription>Segure a liberação de valores pagos para controle adicional antes do repasse aos colaboradores</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/5">
            <div>
              <p className="text-sm font-medium">Custódia para Pessoa Física (PF)</p>
              <p className="text-xs text-muted-foreground mt-1">Valores pagos a colaboradores PF ficam retidos até liberação manual</p>
            </div>
            <Switch data-testid="settings-custody-pf" checked={form.custody_pf} disabled={!canEdit}
              onCheckedChange={(v) => setForm({ ...form, custody_pf: v })} />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/5">
            <div>
              <p className="text-sm font-medium">Custódia para Pessoa Jurídica (PJ)</p>
              <p className="text-xs text-muted-foreground mt-1">Valores pagos a prestadores PJ ficam retidos até liberação manual</p>
            </div>
            <Switch data-testid="settings-custody-pj" checked={form.custody_pj} disabled={!canEdit}
              onCheckedChange={(v) => setForm({ ...form, custody_pj: v })} />
          </div>
          <p className="text-[10px] text-muted-foreground">Colaboradores individuais podem ter a custódia configurada separadamente no cadastro (Herdar / Ativada / Desativada).</p>
        </CardContent>
      </Card>

      <Card className="border-white/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-orange-400" /> Bloqueio de Dados de Pagamento
          </CardTitle>
          <CardDescription>Impeça que colaboradores alterem seus dados bancários/PIX no perfil. A empresa mantém controle total.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/5">
            <div>
              <p className="text-sm font-medium">Bloquear alteração para Pessoa Física (PF)</p>
              <p className="text-xs text-muted-foreground mt-1">Colaboradores PF não poderão alterar dados bancários ou chave PIX</p>
            </div>
            <Switch data-testid="settings-lock-bank-pf" checked={form.lock_employee_bank_pf} disabled={!canEdit}
              onCheckedChange={(v) => setForm({ ...form, lock_employee_bank_pf: v })} />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/5">
            <div>
              <p className="text-sm font-medium">Bloquear alteração para Pessoa Jurídica (PJ)</p>
              <p className="text-xs text-muted-foreground mt-1">Prestadores PJ não poderão alterar dados bancários ou chave PIX</p>
            </div>
            <Switch data-testid="settings-lock-bank-pj" checked={form.lock_employee_bank_pj} disabled={!canEdit}
              onCheckedChange={(v) => setForm({ ...form, lock_employee_bank_pj: v })} />
          </div>
          <p className="text-[10px] text-muted-foreground">Quando ativado, apenas a empresa pode alterar os dados de pagamento do colaborador.</p>
        </CardContent>
      </Card>

      <Card className="border-white/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileDown className="w-4 h-4 text-emerald-400" /> Dados para CNAB (Remessa Bancária)
          </CardTitle>
          <CardDescription>Preencha para gerar arquivos CNAB 240/400 de pagamento de folha. Dados do banco pagador (conta da empresa).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Código do Banco</Label>
              {/* O `maxLength={10}` original deixava digitar o ISPB ("60701190") e o
                  `_rjust_zero` do gerador de CNAB truncava para "607" em silêncio.
                  TRANSFORMAR agora NÃO EXISTE em ponto nenhum deste campo: nem no
                  load, nem em `maxLength`, nem em `.slice(0, 3)`, nem no filtro de
                  dígitos que ele ainda tinha. O `.slice` era o vetor conhecido (um
                  backspace no legado "60701190" dava "607", COMPE válido de OUTRO
                  banco, e LIBERAVA o save); o filtro de dígitos é o mesmo vetor mais
                  raro — "3a41" vira "341", válido e de outro banco. Regra ÚNICA da
                  seção, do parecer OC: em campo que endereça dinheiro, nenhuma
                  transformação silenciosa; valor cru + erro bloqueante que ensina. */}
              <Input data-testid="cnab-bank-code" value={form.cnab_bank_code ?? ""} disabled={!canEdit}
                onChange={(e) => setForm({ ...form, cnab_bank_code: e.target.value })}
                aria-invalid={cnabBankCodeInvalid || undefined}
                aria-describedby={cnabBankCodeInvalid ? "cnab-bank-code-error" : undefined}
                placeholder="001"
                className={`bg-black/20 font-mono ${cnabBankCodeInvalid ? "border-red-500/60" : "border-white/10"}`} />
              {cnabBankCodeInvalid && (
                <p id="cnab-bank-code-error" className="text-[11px] text-red-400" data-testid="cnab-bank-code-error">
                  O valor gravado &quot;{form.cnab_bank_code}&quot; está fora do padrão COMPE-3 —
                  informe o código de 3 dígitos do banco pagador (ex.: 341).
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Convênio</Label>
              <Input data-testid="cnab-convenio" value={form.cnab_convenio} disabled={!canEdit}
                onChange={(e) => setForm({ ...form, cnab_convenio: e.target.value })}
                placeholder="Código convênio" className="bg-black/20 border-white/10 font-mono" maxLength={20} />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">
                Agência <span className="text-red-400" aria-hidden="true">*</span>
              </Label>
              {/* ZERO transformação silenciosa: sem `maxLength`, sem `.slice()` e
                  — GRAVE do parecer OC — sem o filtro de dígitos que este campo
                  chegou a ter. O vetor é o mesmo do `.slice(0, 3)` do código do
                  banco, e aqui ele é MUITO mais provável: o legado da agência
                  costuma trazer o dígito verificador junto ("0001-2"), e uma
                  única tecla no campo refiltrava a string inteira para "00012" —
                  uma agência VÁLIDA, DIFERENTE, com o save destravado e o
                  dinheiro endereçado a outro lugar. Sem filtro, o valor entra
                  CRU e o erro bloqueante abaixo ensina o formato. */}
              <Input data-testid="cnab-agency" value={form.cnab_agency ?? ""} disabled={!canEdit}
                onChange={(e) => setForm({ ...form, cnab_agency: e.target.value })}
                aria-invalid={cnabAgencyInvalid || undefined}
                aria-describedby={
                  cnabAgencyInvalid ? "cnab-agency-error"
                    : cnabAgencyMissing ? "cnab-agency-required" : undefined}
                placeholder="0001"
                className={`bg-black/20 font-mono ${cnabAgencyInvalid ? "border-red-500/60" : "border-white/10"}`} />
              {cnabAgencyInvalid && (
                <p id="cnab-agency-error" className="text-[11px] text-red-400" data-testid="cnab-agency-error">
                  O valor gravado &quot;{cnabAgency}&quot; está fora do padrão —
                  informe de 1 a 5 dígitos da agência (ex.: 0102), sem o dígito verificador.
                </p>
              )}
              {!cnabAgencyInvalid && cnabAgencyMissing && (
                <p id="cnab-agency-required" className="text-[11px] text-amber-400" data-testid="cnab-agency-required">
                  Obrigatória para gerar o arquivo CNAB — sem ela a geração falha.
                  As demais configurações continuam salvando.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Dígito Ag.</Label>
              <Input data-testid="cnab-agency-digit" value={form.cnab_agency_digit} disabled={!canEdit}
                onChange={(e) => setForm({ ...form, cnab_agency_digit: e.target.value })}
                placeholder="0" className="bg-black/20 border-white/10 font-mono" maxLength={2} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Conta <span className="text-red-400" aria-hidden="true">*</span>
              </Label>
              {/* Mesmo desenho ratificado do vizinho (agência): valor CRU, sem
                  `maxLength`, sem filtro no `onChange`. Ver o topo do arquivo. */}
              <Input data-testid="cnab-account" value={form.cnab_account ?? ""} disabled={!canEdit}
                onChange={(e) => setForm({ ...form, cnab_account: e.target.value })}
                aria-invalid={cnabAccountInvalid || undefined}
                aria-describedby={
                  cnabAccountInvalid ? "cnab-account-error"
                    : cnabAccountMissing ? "cnab-account-required" : undefined}
                placeholder="0012345"
                className={`bg-black/20 font-mono ${cnabAccountInvalid ? "border-red-500/60" : "border-white/10"}`} />
              {cnabAccountInvalid && (
                <p id="cnab-account-error" className="text-[11px] text-red-400" data-testid="cnab-account-error">
                  O valor gravado &quot;{cnabAccount}&quot; está fora do padrão —
                  informe de 1 a 12 dígitos da conta (ex.: 0012345), sem o dígito verificador.
                </p>
              )}
              {!cnabAccountInvalid && cnabAccountMissing && (
                <p id="cnab-account-required" className="text-[11px] text-amber-400" data-testid="cnab-account-required">
                  Obrigatória para gerar o arquivo CNAB — sem ela a geração falha.
                  As demais configurações continuam salvando.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Dígito Cta.</Label>
              <Input data-testid="cnab-account-digit" value={form.cnab_account_digit} disabled={!canEdit}
                onChange={(e) => setForm({ ...form, cnab_account_digit: e.target.value })}
                placeholder="0" className="bg-black/20 border-white/10 font-mono" maxLength={2} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nome da Empresa no CNAB (max 30 caracteres)</Label>
            <Input data-testid="cnab-company-name" value={form.cnab_company_name} disabled={!canEdit}
              onChange={(e) => setForm({ ...form, cnab_company_name: e.target.value.slice(0, 30) })}
              placeholder={company?.name?.slice(0, 30) || "Nome da empresa"}
              className="bg-black/20 border-white/10" maxLength={30} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-400" /> Fluxo de Pagamento
          </CardTitle>
          <CardDescription>Configure como e quando os colaboradores recebem seus pagamentos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <Label className="text-sm font-semibold">Pessoa Física (PF)</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Tipo</Label>
                <Select value={form.payment_flow_pf_type} disabled={!canEdit}
                  onValueChange={(v) => setForm({ ...form, payment_flow_pf_type: v })}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="settings-pf-flow-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business_day">Dia útil do mês seguinte</SelectItem>
                    <SelectItem value="fixed_day">Dia fixo do mês seguinte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {form.payment_flow_pf_type === "business_day" ? "Qual dia útil?" : "Qual dia?"}
                </Label>
                <div className="relative">
                  <Input data-testid="settings-pf-flow-day" type="number" min="1" max="28"
                    value={form.payment_flow_pf_day} disabled={!canEdit}
                    onChange={(e) => setForm({ ...form, payment_flow_pf_day: e.target.value })}
                    className="bg-black/20 border-white/10 pr-16" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                    {form.payment_flow_pf_type === "business_day" ? "º dia útil" : "º dia"}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {form.payment_flow_pf_type === "business_day"
                ? `Pagamento no ${form.payment_flow_pf_day}º dia útil do mês seguinte à competência`
                : `Pagamento no dia ${form.payment_flow_pf_day} do mês seguinte à competência`}
            </p>
            <div className="flex items-center justify-between p-3 rounded-lg bg-black/10 border border-white/5">
              <span className="text-xs text-muted-foreground">Ao dia útil cair em sábado ou feriado, antecipar pagamento para o dia útil anterior?</span>
              <Switch data-testid="settings-anticipate-pf" checked={form.anticipate_pf}
                disabled={!canEdit} onCheckedChange={(v) => setForm({ ...form, anticipate_pf: v })} />
            </div>
          </div>

          <div className="border-t border-white/5" />

          <div className="space-y-4">
            <Label className="text-sm font-semibold">Pessoa Jurídica (PJ)</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Tipo</Label>
                <Select value={form.payment_flow_pj_type} disabled={!canEdit}
                  onValueChange={(v) => setForm({ ...form, payment_flow_pj_type: v })}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="settings-pj-flow-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business_day">Dia útil do mês seguinte</SelectItem>
                    <SelectItem value="fixed_day">Dia fixo do mês seguinte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {form.payment_flow_pj_type === "business_day" ? "Qual dia útil?" : "Qual dia?"}
                </Label>
                <div className="relative">
                  <Input data-testid="settings-pj-flow-day" type="number" min="1" max="28"
                    value={form.payment_flow_pj_day} disabled={!canEdit}
                    onChange={(e) => setForm({ ...form, payment_flow_pj_day: e.target.value })}
                    className="bg-black/20 border-white/10 pr-16" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                    {form.payment_flow_pj_type === "business_day" ? "º dia útil" : "º dia"}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {form.payment_flow_pj_type === "business_day"
                ? `Pagamento no ${form.payment_flow_pj_day}º dia útil do mês seguinte à competência`
                : `Pagamento no dia ${form.payment_flow_pj_day} do mês seguinte à competência`}
            </p>
            <div className="flex items-center justify-between p-3 rounded-lg bg-black/10 border border-white/5">
              <span className="text-xs text-muted-foreground">Ao dia útil cair em sábado ou feriado, antecipar pagamento para o dia útil anterior?</span>
              <Switch data-testid="settings-anticipate-pj" checked={form.anticipate_pj}
                disabled={!canEdit} onCheckedChange={(v) => setForm({ ...form, anticipate_pj: v })} />
            </div>
          </div>
        </CardContent>
      </Card>

      {user?.is_master && <ApiKeysSection canEdit={canEdit} />}

      <ChangePassword isCompanyUser={true} />

      {/* Spacer for fixed save bar */}
      {hasChanges && <div className="h-20" />}

      {/* Fixed save bar */}
      {canEdit && hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-background/95 backdrop-blur-sm animate-fade-in" data-testid="save-bar">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-yellow-400">Você tem alterações não salvas</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="border-white/10 text-xs"
                onClick={() => { setForm({ ...originalForm }); }}>
                Descartar
              </Button>
              <Button data-testid="save-settings-btn" size="sm" onClick={handleSave} disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs">
                {saving ? <div className="loading-spinner" /> : <><Save className="w-4 h-4 mr-1.5" /> Salvar Configurações</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApiKeysSection({ canEdit }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState("");
  const [isSandbox, setIsSandbox] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchKeys = useCallback(async () => {
    try { const res = await api.get("/api-keys"); setKeys(res.data); } catch (err) { toast.error(handleApiError(err, "Erro ao carregar API Keys")); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!description.trim()) { toast.error("Informe uma descrição para a key"); return; }
    setCreating(true);
    try {
      const res = await api.post("/api-keys", { description, is_sandbox: isSandbox });
      setNewKey(res.data.raw_key);
      setDescription("");
      setShowForm(false);
      fetchKeys();
      toast.success(`API Key ${isSandbox ? "sandbox" : "de produção"} criada!`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar key"); }
    setCreating(false);
  };

  const handleRevoke = async (keyId, keyPrefix) => {
    if (!window.confirm(`Desativar a key ${keyPrefix}...? Ela deixará de funcionar imediatamente.`)) return;
    try {
      await api.patch(`/api-keys/${keyId}/revoke`);
      toast.success("Key desativada");
      fetchKeys();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const handleDelete = async (keyId, keyPrefix) => {
    if (!window.confirm(`Excluir permanentemente a key ${keyPrefix}...? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/api-keys/${keyId}`);
      toast.success("Key excluída permanentemente");
      fetchKeys();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const copyKey = () => { navigator.clipboard.writeText(newKey); toast.success("Copiada!"); };

  const liveKeys = keys.filter(k => !k.is_sandbox);
  const sandboxKeys = keys.filter(k => k.is_sandbox);

  return (
    <Card className="border-white/5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Key className="w-4 h-4 text-purple-400" /> API Keys</CardTitle>
            <CardDescription>Gere chaves para integração com sistemas externos</CardDescription>
          </div>
          {canEdit && !showForm && (
            <Button data-testid="new-api-key-btn" onClick={() => setShowForm(true)} size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nova Key
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* New key display */}
        {newKey && (
          <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20 space-y-3" data-testid="new-key-display">
            <p className="text-xs font-semibold text-purple-400">Copie agora — esta key não será exibida novamente!</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-2.5 bg-black/30 rounded-md text-xs font-mono break-all select-all">{newKey}</code>
              <Button size="sm" variant="ghost" onClick={copyKey} className="shrink-0 h-9 w-9" data-testid="copy-new-key"><Copy className="w-4 h-4" /></Button>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setNewKey(null)} className="text-xs text-muted-foreground">Fechar</Button>
          </div>
        )}

        {/* Create form */}
        {showForm && canEdit && (
          <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02] space-y-4" data-testid="create-key-form">
            <p className="text-sm font-medium">Nova API Key</p>
            <Input data-testid="api-key-description" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição (ex: Sistema RH XYZ)" className="bg-black/20 border-white/10" />
            <div className="flex items-center gap-3">
              <button data-testid="key-type-live" onClick={() => setIsSandbox(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${!isSandbox ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-white/10 text-zinc-500 hover:border-white/20"}`}>
                <Shield className="w-3.5 h-3.5" /> Produção
              </button>
              <button data-testid="key-type-sandbox" onClick={() => setIsSandbox(true)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${isSandbox ? "border-amber-500/40 bg-amber-500/10 text-amber-400" : "border-white/10 text-zinc-500 hover:border-white/20"}`}>
                <FlaskConical className="w-3.5 h-3.5" /> Sandbox
              </button>
            </div>
            {isSandbox && (
              <p className="text-[11px] text-amber-400/70 leading-relaxed">
                Keys sandbox simulam operações financeiras (PIX, transfers, antecipações) sem movimentar dinheiro real. Prefixo: <code className="text-amber-400">sk_sandbox_</code>
              </p>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button data-testid="create-api-key-btn" onClick={handleCreate} disabled={creating} size="sm"
                className={`font-semibold text-white ${isSandbox ? "bg-amber-600 hover:bg-amber-700" : "bg-purple-600 hover:bg-purple-700"}`}>
                {creating ? <div className="loading-spinner" /> : `Gerar Key ${isSandbox ? "Sandbox" : "Produção"}`}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setDescription(""); }} className="text-xs text-muted-foreground">Cancelar</Button>
            </div>
          </div>
        )}

        {loading ? <div className="flex justify-center py-6"><div className="loading-spinner w-5 h-5" /></div> : (
          <>
            {/* Production keys */}
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-2 flex items-center gap-1.5">
                <Shield className="w-3 h-3 text-emerald-400" /> Produção
                <span className="text-zinc-600">({liveKeys.filter(k => k.active).length} ativa{liveKeys.filter(k => k.active).length !== 1 ? "s" : ""})</span>
              </p>
              {liveKeys.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3 border border-dashed border-white/10 rounded-lg">Nenhuma key de produção</p>
              ) : (
                <div className="space-y-1.5">
                  {liveKeys.map(k => (
                    <KeyRow key={k.id} k={k} canEdit={canEdit} onRevoke={handleRevoke} onDelete={handleDelete} />
                  ))}
                </div>
              )}
            </div>

            {/* Sandbox keys */}
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-2 flex items-center gap-1.5">
                <FlaskConical className="w-3 h-3 text-amber-400" /> Sandbox
                <span className="text-zinc-600">({sandboxKeys.filter(k => k.active).length} ativa{sandboxKeys.filter(k => k.active).length !== 1 ? "s" : ""})</span>
              </p>
              {sandboxKeys.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3 border border-dashed border-white/10 rounded-lg">Nenhuma key sandbox</p>
              ) : (
                <div className="space-y-1.5">
                  {sandboxKeys.map(k => (
                    <KeyRow key={k.id} k={k} canEdit={canEdit} onRevoke={handleRevoke} onDelete={handleDelete} isSandbox />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="text-xs text-muted-foreground space-y-1 pt-3 border-t border-white/5">
          <p><strong>Base URL:</strong> <code>https://api.kredz.com.br/api/v1/public/</code></p>
          <p><strong>Auth:</strong> Header <code>X-API-Key: sua_key_aqui</code></p>
          <p className="pt-1"><a href="/docs" target="_blank" className="text-blue-400 hover:text-blue-300 font-medium">Ver documentação completa</a>
            {" · "}<a href="https://api.kredz.com.br/api/v1/public/docs" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium">Swagger UI</a></p>
        </div>
      </CardContent>
    </Card>
  );
}

function KeyRow({ k, canEdit, onRevoke, onDelete, isSandbox }) {
  const created = k.created_at ? new Date(k.created_at).toLocaleDateString("pt-BR") : "";
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border transition-all ${k.active
      ? (isSandbox ? "bg-amber-500/[0.03] border-amber-500/10" : "bg-white/[0.02] border-white/[0.06]")
      : "bg-black/10 border-white/[0.03] opacity-50"
    }`} data-testid={`api-key-${k.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono truncate">{k.key_prefix}...</code>
          {k.active
            ? <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Ativa</span>
            : <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20">Desativada</span>
          }
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{k.description || "Sem descrição"}{created ? ` · ${created}` : ""}</p>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {k.active && (
            <Button variant="ghost" size="icon" onClick={() => onRevoke(k.id, k.key_prefix)}
              data-testid={`revoke-key-${k.id}`}
              className="h-8 w-8 text-muted-foreground hover:text-amber-400" title="Desativar key">
              <Power className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => onDelete(k.id, k.key_prefix)}
            data-testid={`delete-key-${k.id}`}
            className="h-8 w-8 text-muted-foreground hover:text-red-400" title="Excluir permanentemente">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}